import { FormEvent, useRef, useState } from 'react';
import { links } from '../../config/links';
import styles from './RequestDemoForm.module.css';

interface FormData {
  name: string;
  email: string;
  venue: string;
  role: string;
  message: string;
  companyWebsite: string;
  faxNumber: string;
}

const initialFormData: FormData = {
  name: '',
  email: '',
  venue: '',
  role: '',
  message: '',
  companyWebsite: '',
  faxNumber: '',
};

function RequestDemoForm() {
  const formLoadedAt = useRef(Date.now());
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch(links.demoApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          formLoadedAt: formLoadedAt.current,
        }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Unable to send your demo request. Please try again.');
      }

      setFormData(initialFormData);
      setStatus('success');
    } catch (error) {
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Unable to send your demo request. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div className={styles.success} role="status">
        <h2>Thanks — we received your request.</h2>
        <p>Our team will reach out shortly to schedule your ONI demo.</p>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <div className={styles.grid}>
        <label className={styles.field}>
          <span>Name *</span>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={(event) => handleChange('name', event.target.value)}
            required
            autoComplete="name"
          />
        </label>

        <label className={styles.field}>
          <span>Email *</span>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={(event) => handleChange('email', event.target.value)}
            required
            autoComplete="email"
          />
        </label>

        <label className={styles.field}>
          <span>Venue / Organization *</span>
          <input
            type="text"
            name="venue"
            value={formData.venue}
            onChange={(event) => handleChange('venue', event.target.value)}
            required
          />
        </label>

        <label className={styles.field}>
          <span>Role *</span>
          <input
            type="text"
            name="role"
            value={formData.role}
            onChange={(event) => handleChange('role', event.target.value)}
            required
          />
        </label>
      </div>

      <label className={styles.field}>
        <span>Message</span>
        <textarea
          name="message"
          rows={4}
          value={formData.message}
          onChange={(event) => handleChange('message', event.target.value)}
          placeholder="Tell us about your venue, upcoming season, or what you want to see in a demo."
        />
      </label>

      <label className={styles.honeypot} aria-hidden="true">
        <span>Company website</span>
        <input
          type="text"
          name="companyWebsite"
          tabIndex={-1}
          autoComplete="nope"
          value={formData.companyWebsite}
          onChange={(event) => handleChange('companyWebsite', event.target.value)}
        />
      </label>

      <label className={styles.honeypot} aria-hidden="true">
        <span>Fax number</span>
        <input
          type="text"
          name="faxNumber"
          tabIndex={-1}
          autoComplete="nope"
          value={formData.faxNumber}
          onChange={(event) => handleChange('faxNumber', event.target.value)}
        />
      </label>

      {status === 'error' && (
        <p className={styles.error} role="alert">
          {errorMessage}
        </p>
      )}

      <button type="submit" className={styles.submit} disabled={status === 'submitting'}>
        {status === 'submitting' ? 'Sending...' : 'Request Demo'}
      </button>
    </form>
  );
}

export default RequestDemoForm;
